import { ApolloError, useQuery } from '@apollo/client';
import { Door, Location, Query, Visitor, VisitorGroupsFilter } from 'graphql/generated/graphqlTypes';
import {
  GET_ALL_VISITORS_GROUPS,
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_WITHOUT_NESTED_RESOLVERS,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITORS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';

export const refetchQueries = [
  { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_VISITORS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_DOORS_WITHOUT_NESTED_RESOLVERS }
];

type UseVisitorsGroupsCacheProps = BaseHookProps & {
  filter?: VisitorGroupsFilter;
};

const useVisitorsGroupsCache = ({ handleFetchError, filter }: UseVisitorsGroupsCacheProps) => {
  const { translation } = useTranslation();

  const handleError = (message: string) => (error: ApolloError) => {
    handleFetchError(message);
    console.error(error);
  };

  const queryOptions = { variables: { filter }, fetchPolicy: 'cache-first' as const };

  // Warm up the cache — each query requests only ids for nested entities, so no nested resolver runs.
  const {
    loading: groupsWarmupLoading,
    data: groupsData,
    error: groupsWarmupError
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>, { filter?: VisitorGroupsFilter }>(
    GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
    queryOptions
  );

  useEffect(() => {
    if (groupsWarmupError) handleError(translation.user_groups_error_fetching)(groupsWarmupError);
  }, [groupsWarmupError]);

  const {
    loading: locationsWarmupLoading,
    data: locationsData,
    error: locationsWarmupError
  } = useQuery<Pick<Query, 'getLocationsForTenant'>>(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS);

  useEffect(() => {
    if (locationsWarmupError) handleError(translation.device_group_error_fetching)(locationsWarmupError);
  }, [locationsWarmupError]);

  const {
    loading: visitorsWarmupLoading,
    data: visitorsData,
    error: visitorsWarmupError
  } = useQuery<Pick<Query, 'getVisitors'>>(GET_VISITORS_WITHOUT_NESTED_RESOLVERS);

  useEffect(() => {
    if (visitorsWarmupError) handleError(translation.error_fetching_users)(visitorsWarmupError);
  }, [visitorsWarmupError]);

  const {
    loading: doorsWarmupLoading,
    data: doorsData,
    error: doorsWarmupError
  } = useQuery<Pick<Query, 'getDoorsForTenant'>>(GET_DOORS_WITHOUT_NESTED_RESOLVERS);

  useEffect(() => {
    if (doorsWarmupError) handleError(translation.devices_error_fetching)(doorsWarmupError);
  }, [doorsWarmupError]);

  const warmingUp =
    (groupsWarmupLoading && !groupsData) ||
    (locationsWarmupLoading && !locationsData) ||
    (visitorsWarmupLoading && !visitorsData) ||
    (doorsWarmupLoading && !doorsData);

  // Read the fully stitched shape straight from the normalized cache once warm-up is done.
  const {
    loading: composedLoading,
    data: allVisitorGroupsData,
    error: composedError
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>, { filter?: VisitorGroupsFilter }>(GET_ALL_VISITORS_GROUPS, {
    variables: { filter },
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: warmingUp
  });

  useEffect(() => {
    if (composedError) handleError(translation.user_groups_error_fetching)(composedError);
  }, [composedError]);

  // The warm-up queries fetch only ids (`memberIds`, `locationIds`, `doorIds`) to avoid running nested
  // resolvers, so stitch the nested entities back in from the ones already held in the cache.
  const allVisitorGroups = useMemo(() => {
    // While warming up the id-only maps are empty, so stitching would silently drop nested
    // entities — return nothing until the cache is warm rather than incomplete data.
    if (warmingUp) return [];

    const groups = allVisitorGroupsData?.getVisitorGroupsForTenant ?? [];
    const visitorsById = new Map((visitorsData?.getVisitors ?? []).map((visitor) => [visitor.id, visitor]));
    const locationsById = new Map(
      (locationsData?.getLocationsForTenant ?? []).map((location) => [location.id, location])
    );
    const doorsById = new Map((doorsData?.getDoorsForTenant ?? []).map((door) => [door.id, door]));

    return groups.map((group) => ({
      ...group,
      members: (group.memberIds ?? [])
        .map((memberId) => visitorsById.get(memberId))
        .filter((visitor): visitor is Visitor => Boolean(visitor)),
      accessGrants: (group.accessGrants ?? []).map((accessGrant) => ({
        ...accessGrant,
        locations: (accessGrant.locationIds ?? [])
          .map((locationId) => locationsById.get(locationId))
          .filter((location): location is Location => Boolean(location)),
        doors: (accessGrant.doorIds ?? [])
          .map((doorId) => doorsById.get(doorId))
          .filter((door): door is Door => Boolean(door))
      }))
    }));
  }, [warmingUp, allVisitorGroupsData, visitorsData, locationsData, doorsData]);

  return {
    allVisitorGroups,
    loadingAllVisitorGroups: warmingUp || composedLoading
  };
};

export default useVisitorsGroupsCache;
